import { ChromecastUser } from '@tv2-player/unifiedplayer/src/types/ChromecastUser';
// eslint-disable-next-line monorepo-dependencies/no-unlisted-package-import
import { LoadRequestData } from 'chromecast-caf-receiver/cast.framework.messages';
// eslint-disable-next-line monorepo-dependencies/no-unlisted-package-import
import { PlayerManager } from 'chromecast-caf-receiver/cast.framework';
import type { ChromecastPlayRequestCustomData } from '../../types';
import { getChromecastUser } from '../../utils/getChromecastUser';
import { getChromecastMediaCapabilities } from '../../utils/deviceDetector';
import { fetchMedia, makeMediaObject, mapMediaToMediaInformation } from '../../services/media';
import cast from '../../types/chromecast-caf-receiver/index';
import store, { CONTENT_ID, REQUEST_ID, USER_INFORMATION } from '../../store';
import {
  beforeFetchMediaAction,
  bootstrapAction,
  errorAction,
  loadRequestFilter,
  mediaFilter,
} from '../extensions';

export class PlaybackPlugin {
  user: ChromecastUser;
  playerManager: PlayerManager;

  init() {
    bootstrapAction.register(async context => {
      this.playerManager = context.getPlayerManager();
    });
    loadRequestFilter.register(this.loadTransformer.bind(this));
  }

  async loadTransformer(loadRequestData: LoadRequestData): Promise<LoadRequestData> {
    if (loadRequestData.type === 'PRELOAD') return loadRequestData;
    if (loadRequestData.media.contentUrl) return loadRequestData;

    const customData = loadRequestData.customData as ChromecastPlayRequestCustomData;

    this.user = getChromecastUser(customData);

    store.set(CONTENT_ID, loadRequestData.media.contentId);
    store.set(USER_INFORMATION, this.user);
    store.set(REQUEST_ID, loadRequestData.requestId);

    const mediaCapabilities = await getChromecastMediaCapabilities();
    await beforeFetchMediaAction.perform(loadRequestData);
    try {
      const mediaResponse = await fetchMedia(
        loadRequestData.media.contentId,
        loadRequestData.requestId.toString(),
        this.user.accessToken,
        this.user,
        mediaCapabilities
      );
      const mediaObject = makeMediaObject(
        mediaResponse,
        this.user,
        loadRequestData,
        mediaCapabilities
      );

      const media = await mediaFilter.apply(mediaObject, customData.accessToken);

      const breakManager = this.playerManager.getBreakManager();
      // To be able to watch commercials on next playback-request, we need to "setPlayWatchedBreak"
      // But let's base it on whether the media is live or not
      breakManager.setPlayWatchedBreak(!media.metadata.isLive);

      return {
        ...loadRequestData,
        media: mapMediaToMediaInformation(
          media,
          Boolean(loadRequestData.currentTime && loadRequestData.currentTime > 0)
        ),
        customData: { ...loadRequestData.customData, security: media.drm, format: media.format },
      };
    } catch (e) {
      errorAction.perform(e);
      return new cast.framework.messages.ErrorData(
        cast.framework.messages.ErrorType.LOAD_FAILED
      ) as any;
    }
  }
}
